import React, { useState, useEffect, useCallback } from 'react';
import { localToday } from '../../utils/dateUtils';
import './saude.css';
import Sidebar from '../../components/Sidebar';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClock } from '@fortawesome/free-solid-svg-icons';
import { useLocation } from 'react-router-dom';
import { useInternals } from '../../context/internalContext';
import { useAuxiliaries } from '../../context/auxiliariesContext';
import { useUsers } from '../../context/usersContext';
import { toast } from 'react-toastify';
import Header from '../../components/header';
import Titulo from '../../components/Titulo/titulo';
import PageHeader from '../../components/PageHeader';
import File from '../../assets/icons/file.svg';
import SearchInternal from '../../components/SearchInternal/SearchInternal';
import InternalDataSkeleton from '../../components/InternalDataSkeleton';
import useSubmitLock from '../../utils/useSubmitLock';
import BinaryFileLink from '../../components/BinaryFileLink';
import { BINARY_RESOURCES, hasBinaryField } from '../../utils/binaryFile';

const initializeFormData = (userInfo) => ({
  internal: '',
  date: localToday(),
  evolution_date: localToday(),
  mental_disorder: [],
  psychological_treatment: '',
  mental_health: '',
  linguage: '',
  thinking: '',
  physical_appearance: '',
  hygiene: '',
  observation: '',
  want_service: false,
  diagnosis: [],
  file: null,
  evolution: '',
  psychologist: userInfo?.id || '',
});

const Psychology = () => {
  const location = useLocation();
  const { internalId } = location.state || {};
  const { userInfo, effectiveUnitId, buildUnitReadParams } = useUsers();
  const {
    listInternals,
    addPsychology,
    listPsychology,
    updatePsychology,
    addPsychologicalCare,
    listPsychologicalCare,
    listAdmissions,
    listPrescriptions,
  } = useInternals();

  const { listMentalDisorders, listDiagnoses } = useAuxiliaries();

  const [diagnoses, setDiagnoses] = useState([]);
  const [internalDetails, setInternalDetails] = useState(null);
  const [latestAdmission, setLatestAdmission] = useState(null);
  const [currentTab, setCurrentTab] = useState('dados');
  const [evolutions, setEvolutions] = useState([]);
  const [prescriptions, setPrescriptions] = useState([]);
  const [formData, setFormData] = useState(() => initializeFormData(userInfo));
  const [isSelectingInternal, setIsSelectingInternal] = useState(false);
  const {
    isSubmitting: isSavingPsychology,
    runWithSubmitLock: runSavePsychology,
  } = useSubmitLock();
  const {
    isSubmitting: isSavingEvolution,
    runWithSubmitLock: runSaveEvolution,
  } = useSubmitLock();

  const normalizeUpperNoPunct = (value) => {
    if (!value) return value;
    const upper = String(value).toUpperCase();
    const noAccents = upper.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    return noAccents.replace(/[^A-Z0-9\s]/g, '');
  };

  const normalizeUpper = (value) => {
    if (!value) return value;
    return String(value).toUpperCase();
  };

  const formatBooleanPt = (value) => (value ? 'SIM' : 'NAO');

  const formatDrugList = (drugs) => {
    if (!Array.isArray(drugs) || !drugs.length) return 'NAO INFORMADO';
    return drugs.map((item) => item?.name || '').filter(Boolean).join(', ') || 'NAO INFORMADO';
  };

  const loadLatestAdmission = useCallback(async (internalIdValue) => {
    if (!internalIdValue) {
      setLatestAdmission(null);
      return;
    }

    try {
      const res = await listAdmissions({ internal__id: internalIdValue });
      const items = Array.isArray(res) ? res : res?.results || [];
      if (!items.length) {
        setLatestAdmission(null);
        return;
      }

      const latest = [...items].sort((a, b) => {
        const aDate = new Date(a?.updated_at || a?.created_at || 0).getTime();
        const bDate = new Date(b?.updated_at || b?.created_at || 0).getTime();
        return bDate - aDate;
      })[0];

      setLatestAdmission(latest || null);
    } catch (error) {
      console.error('Erro ao carregar admissão para psicologia:', error);
      setLatestAdmission(null);
    }
  }, [listAdmissions]);

  const loadAuxiliaryData = useCallback(async () => {
    try {
      const [, diagnosesData] = await Promise.all([
        listMentalDisorders(),
        listDiagnoses(),
      ]);
      setDiagnoses(diagnosesData?.results || []);
    } catch (error) {
      console.error('Erro ao carregar dados auxiliares:', error);
      toast.error('Erro ao carregar dados auxiliares.');
    }
  }, [listMentalDisorders, listDiagnoses]);

  const handleInternalSelect = useCallback(async (internal) => {
    try {
      const psychologyResponse = await listPsychology(internal.id);
      const psychologyData = psychologyResponse?.results?.[0] || initializeFormData(userInfo);

      setFormData({
        ...psychologyData,
        internal: internal.id,
      });

      setInternalDetails(internal);

      const evolutionResponse = await listPsychologicalCare(internal.id);
      setEvolutions(evolutionResponse?.results || []);

      const prescriptionsResponse = await listPrescriptions(internal.id);
      setPrescriptions(prescriptionsResponse?.results || []);
      await loadLatestAdmission(internal.id);
    } catch (error) {
      console.error('Erro ao carregar dados do interno:', error);
      toast.error('Erro ao carregar dados do interno.');
    }
  }, [listPsychology, listPsychologicalCare, listPrescriptions, loadLatestAdmission, userInfo]);


  // Efeito para carregar dados auxiliares
  useEffect(() => {
    if (effectiveUnitId !== null && effectiveUnitId !== undefined && String(effectiveUnitId) !== '') {
      loadAuxiliaryData();
    }
  }, [loadAuxiliaryData, effectiveUnitId]);


  const handleInputChange = (e) => {
    const { name, value, type, files, checked } = e.target;
    const normalizedFieldsWithoutPunct = new Set([
      'psychological_treatment',
    ]);
    const normalizedFieldsKeepingPunct = new Set([
      'mental_health',
      'evolution',
      'observation',
    ]);
    const nextValue = normalizedFieldsWithoutPunct.has(name)
      ? normalizeUpperNoPunct(value)
      : normalizedFieldsKeepingPunct.has(name)
        ? normalizeUpper(value)
        : value;
    setFormData((prevFormData) => ({
      ...prevFormData,
      [name]: type === 'file' ? files[0] : type === 'checkbox' ? checked : nextValue,
    }));
  };

  useEffect(() => {
    if (internalId) {
      const fetchInternalById = async () => {
        try {
          const response = await listInternals(buildUnitReadParams({ id: internalId }));
          const internalData = response?.results?.[0];
          if (internalData) {
            handleInternalSelect(internalData);
          }
        } catch (error) {
          console.error("Erro ao carregar interno pelo ID:", error);
          toast.error("Erro ao carregar interno.");
        }
      };
      fetchInternalById();
    }
  }, [internalId, handleInternalSelect, listInternals, buildUnitReadParams]);
  const handleSubmit = async (e) => {
    e.preventDefault();
    await runSavePsychology(async () => {
      if (!formData.internal) {
        toast.error('Nenhum interno selecionado.');
        return;
      }

      const requiredFields = [
        'linguage',
        'thinking',
        'physical_appearance',
        'hygiene',
      ];

      // Validação precisa para identificar campos vazios
      for (const field of requiredFields) {
        if (!formData[field] || (Array.isArray(formData[field]) && formData[field].length === 0)) {
          toast.warning('Preencha os campos obrigatórios');
          return;
        }
      }

      const data = new FormData();
      const listFields = new Set(['mental_disorder', 'diagnosis']);

      Object.entries(formData).forEach(([key, value]) => {
        if (key === 'file') {
          if (value) data.append(key, value);
          return;
        }

        if (listFields.has(key)) {
          const values = Array.isArray(value)
            ? value.filter((item) => item !== '' && item !== null && item !== undefined)
            : [];

          values.forEach((item) => {
            data.append(key, String(item));
          });
          return;
        }

        if (typeof value === 'boolean') {
          data.append(key, value ? 'true' : 'false');
          return;
        }

        if (value !== '' && value !== null && value !== undefined) {
          data.append(key, value);
        }
      });

      try {
        await (formData.id ? updatePsychology(formData.id, data) : addPsychology(data));
        if (internalDetails?.id) {
          await handleInternalSelect(internalDetails);
        }
      } catch (error) {
        console.error('Erro ao salvar dados:', error);
      }
    });
  };

  const handleEvolutionSubmit = async () => {
    await runSaveEvolution(async () => {
      if (!formData.internal) {
        toast.error('Nenhum interno selecionado.');
        return;
      }

      if (!formData.evolution_date) {
        toast.error('Informe a data da evolução.');
        return;
      }

      if (!formData.evolution || !formData.evolution.trim()) {
        toast.error('Descreva a evolução antes de salvar.');
        return;
      }

      const data = {
        internal: formData.internal,
        date: `${formData.evolution_date}T00:00:00`,
        data: formData.evolution,
        psychologist: userInfo.id, // Certifique-se de enviar o psicólogo
      };

      try {
        const response = await addPsychologicalCare(data);
        const evolutionResponse = await listPsychologicalCare(formData.internal);

        // Atualiza a lista de evoluções com os dados mais recentes
        setEvolutions(evolutionResponse?.results || []);
        setFormData((prevFormData) => ({
          ...prevFormData,
          evolution: '',
          evolution_date: localToday(),
        }));

        if (response?.status === 200 || response?.status === 201) {
          toast.success('Evolução salva com sucesso!');
        }
      } catch (error) {
        console.error('Erro ao enviar evolução:', error);
        toast.error('Erro ao salvar evolução.');
      }
    });
  };

  const renderTabContent = () => {
    switch (currentTab) {
      case 'dados':
        return renderDadosTab();
      case 'prescricao':
        return renderPrescricaoTab();
      case 'evolucao':
        return renderEvolucaoTab();
      default:
        return <div />;
    }
  };

  const renderDadosTab = () => (
    <div>
      <div className='social-form-group-duplo'>
        <div className='social-form-group'>
          <label>Linguagem *</label>
          <select type="text" name="linguage" value={formData.linguage} onChange={handleInputChange} >
            <option value=''>Selecione</option>
            <option value='Clara'>Clara</option>
            <option value='Objetiva'>Objetiva</option>
            <option value='Confusa'>Confusa</option>
          </select>
        </div>
        <div className='social-form-group'>
          <label>Pensamento *</label>
          <select type="text" name="thinking" value={formData.thinking} onChange={handleInputChange}>
            <option value=''>Selecione</option>
            <option value='Coerente'>Coerente</option>
            <option value='Incoerente'>Incoerente</option>
          </select>
        </div>
      </div>
      <div className='social-form-group-duplo'>
        <div className='social-form-group'>
          <label>Aparência Física *</label>
          <select type="text" name="physical_appearance" value={formData.physical_appearance} onChange={handleInputChange} >
            <option value=''>Selecione</option>
            <option value='Adequada'>Adequada</option>
            <option value='Inadequada'>Inadequada</option>
          </select>
        </div>
        <div className='social-form-group'>
          <label>Higiene *</label>
          <select type="text" name="hygiene" value={formData.hygiene} onChange={handleInputChange}>
            <option value=''>Selecione</option>
            <option value='Bom'>Bom</option>
            <option value='Precario'>Precário</option>
            <option value='Irregular'>Irregular</option>
          </select>
        </div>
      </div>
      <div className='social-form-group-duplo'>
        <div className='social-form-group'>
          <label>Saúde Mental</label>
          <textarea rows={6} type="text" name="mental_health" value={formData.mental_health} onChange={handleInputChange} maxLength={500} />
          <span className="admission-card__hint">
            {(formData.mental_health || '').length}/500 caracteres
            {(formData.mental_health || '').length >= 500 && (
              ' — Limite atingido! Reduza o texto ou continue as informações no campo Observação.'
            )}
          </span>
        </div>
      </div>
      <div className='social-form-group'>
        <label>Deseja Serviço</label>
        <div className="toggle-field">
          <span className={`toggle-label ${!formData.want_service ? 'active' : ''}`}>Não</span>
          <label className="toggle-switch">
            <input
              type="checkbox"
              name="want_service"
              checked={!!formData.want_service}
              onChange={handleInputChange}
            />
            <span className="toggle-slider" />
          </label>
          <span className={`toggle-label ${formData.want_service ? 'active' : ''}`}>Sim</span>
        </div>
      </div>
      <div className='social-form-group'>
        <label>Diagnóstico</label>
        <div className="checkbox-group2">
          {diagnoses.map((diagnosis) => (
            <label key={diagnosis.id}>
              <input
                type="checkbox"
                value={diagnosis.id}
                checked={formData.diagnosis.includes(diagnosis.id)}
                onChange={(e) => {
                  const value = parseInt(e.target.value);
                  const isChecked = e.target.checked;
                  setFormData((prev) => ({
                    ...prev,
                    diagnosis: isChecked
                      ? [...prev.diagnosis, value]
                      : prev.diagnosis.filter((id) => id !== value),
                  }));
                }}
              />
              {diagnosis.name}
            </label>
          ))}
        </div>
      </div>
      <div>
        <div className='social-form-group textarea-full'>
          <label>Observação</label>
          <textarea rows={6} name="observation" value={formData.observation} onChange={handleInputChange} />
        </div>
      </div>

      <div className="line">
        <h4>Arquivo</h4>
        <div className="images-grid">
          {hasBinaryField(formData, 'file') && (
            <BinaryFileLink className='arquivo-incluso' resource={BINARY_RESOURCES.receptionPsychology} id={formData.id} field="file">
              <div style={{ width: 60, height: 60 }} className="image-thumbnail">
                <img src={File} alt="Arquivo" />
              </div>
            </BinaryFileLink>
          )}
          <div className='form-cadastro-interno'>
            <div style={{ width: 200, height: 100 }} className='image-upload-container'>
              <input type='file' name="file" onChange={handleInputChange} className="image-input" />
              <img src={File} alt="Upload icon" className="upload-icon" />
            </div>
          </div>
        </div>
      </div>
      <div>
        <button className="social-submit-button" onClick={handleSubmit} disabled={isSavingPsychology}>
          {isSavingPsychology ? 'Salvando...' : 'Salvar'}
        </button>
      </div>
    </div >
  );

  const renderPrescricaoTab = () => {
    const sortedPrescriptions = [...(prescriptions || [])].sort((a, b) => (
      new Date(b?.created_at || 0).getTime() - new Date(a?.created_at || 0).getTime()
    ));

    return (
      <div>
        <div className="timeline">
          <h4>Histórico de prescrições</h4>
          <ul className="timeline-list">
            {sortedPrescriptions.length > 0 ? (
              sortedPrescriptions.map((item) => (
                <li key={item.id} className="timeline-item">
                  <div className="timeline-date">
                    - {new Date(item.created_at).toLocaleString()}
                  </div>
                  <div className="timeline-content">
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, width: '100%', flex: '1 1 100%' }}>
                      <div className="health-static-field" style={{ flex: '1 1 140px', minHeight: 'auto' }}>
                        <span>Nome</span>
                        <p>{item.name || '-'}</p>
                      </div>
                      <div className="health-static-field" style={{ flex: '1 1 140px', minHeight: 'auto' }}>
                        <span>Dose</span>
                        <p>{item.dosage || '-'}</p>
                      </div>
                      <div className="health-static-field" style={{ flex: '1 1 120px', minHeight: 'auto' }}>
                        <span>Período</span>
                        <p>{item.frequency || '-'}</p>
                      </div>
                      <div className="health-static-field" style={{ flex: '1 1 160px', minHeight: 'auto' }}>
                        <span>Origem</span>
                        <p>{item.origin || '-'}</p>
                      </div>
                      {hasBinaryField(item, 'file') && (
                        <div className="health-static-field" style={{ flex: '1 1 180px', minHeight: 'auto' }}>
                          <span>Documento</span>
                          <p>
                            <BinaryFileLink resource={BINARY_RESOURCES.medicalPrescription} id={item.id} field="file">
                              Ver receita
                            </BinaryFileLink>
                          </p>
                        </div>
                      )}
                    </div>
                    {hasBinaryField(item, 'file') && (
                      <div style={{ marginTop: 8 }}>
                        <BinaryFileLink resource={BINARY_RESOURCES.medicalPrescription} id={item.id} field="file">
                          Abrir documento
                        </BinaryFileLink>
                      </div>
                    )}
                  </div>
                </li>
              ))
            ) : (
              <p>Nenhuma prescrição encontrada.</p>
            )}
          </ul>
        </div>
      </div>
    );
  };

  const renderEvolucaoTab = () => (
    <div className="evolucao-container">
      <div className='social-form-group'>
        <label>Data da evolução</label>
        <input
          type="date"
          name="evolution_date"
          value={formData.evolution_date || ''}
          onChange={handleInputChange}
        />
      </div>
      <div className='social-form-group'>
        <label>Registrar Evolução</label>
        <textarea
          name="evolution"
          rows="4"
          value={formData.evolution || ''}
          onChange={handleInputChange}
          placeholder="Descreva a evolução do interno"
          maxLength={500}
        />
        <span className="admission-card__hint">
          {(formData.evolution || '').length}/500 caracteres
          {(formData.evolution || '').length >= 500 && ' — Limite atingido!'}
        </span>
        <button className="social-submit-button" onClick={handleEvolutionSubmit} disabled={isSavingEvolution}>
          {isSavingEvolution ? 'Salvando...' : 'Adicionar Evolução'}
        </button>
      </div>
      <div className="timeline">
        <h4>Evoluções</h4>
        <ul className="timeline-list">
          {evolutions.length > 0 ? (
            evolutions.map((evo, index) => (
              <li key={index} className="timeline-item">
                <div className="timeline-date">
                  <FontAwesomeIcon icon={faClock} /> {new Date(evo.date).toLocaleString()}
                </div>
                <div className="timeline-content">
                  {evo.data}
                </div>
              </li>
            ))
          ) : (
            <p>Nenhuma evolução registrada.</p>
          )}
        </ul>
      </div>
    </div>
  );

  return (
    <div>
      <Sidebar />
      <Header />
      <div className='container social-container admission-page'>
        <Titulo
          children={
            <SearchInternal
              onSelect={handleInternalSelect}
              onSelectingChange={setIsSelectingInternal}
            />
          }
        />

        <PageHeader
          title="Psicologia"
          subtitle="Evoluções e atendimentos psicológicos."
          internalDetails={internalDetails}
        />

        {isSelectingInternal ? (
          <InternalDataSkeleton />
        ) : (
          <>
        <div className="health-card" style={{ marginTop: 12 }}>
          <div className="health-section-header">
            <h4>Dados da admissão</h4>
          </div>
          {latestAdmission ? (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
              <div className="health-static-field" style={{ flex: '1 1 180px', minHeight: 'auto' }}>
                <span>Usuário de entorpecente</span>
                <p>{formatBooleanPt(!!latestAdmission.drug_user)}</p>
              </div>
              <div className="health-static-field" style={{ flex: '1 1 140px', minHeight: 'auto' }}>
                <span>Etilista</span>
                <p>{formatBooleanPt(!!latestAdmission.alcoholic)}</p>
              </div>
              <div className="health-static-field" style={{ flex: '1 1 140px', minHeight: 'auto' }}>
                <span>Tabagista</span>
                <p>{formatBooleanPt(!!latestAdmission.smoker)}</p>
              </div>
              <div className="health-static-field" style={{ flex: '2 1 320px', minHeight: 'auto' }}>
                <span>Drogas utilizadas</span>
                <p>{formatDrugList(latestAdmission.drugs)}</p>
              </div>
            </div>
          ) : (
            <div className="app-drawer__empty" style={{ marginTop: 8 }}>
              Nenhum dado de admissão encontrado para este interno.
            </div>
          )}
        </div>

        <div className='social-tabs'>
          <button className={`social-tab-button ${currentTab === 'dados' ? 'active' : ''}`} onClick={() => setCurrentTab('dados')}>Dados</button>
          <button className={`social-tab-button ${currentTab === 'prescricao' ? 'active' : ''}`} onClick={() => setCurrentTab('prescricao')}>Prescrição</button>
          <button className={`social-tab-button ${currentTab === 'evolucao' ? 'active' : ''}`} onClick={() => setCurrentTab('evolucao')}>Evolução</button>
        </div>

        <div className='social-tab-content'>
          {renderTabContent()}
        </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Psychology;
